import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Dropdown, Form, Grid, Label, Segment, Dimmer, Loader, Button, Input, Divider, Icon, Checkbox, Modal, Header, List, Radio } from "semantic-ui-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as DEEPANALYSIS_ACTIONS from "../../../actions/deepanalysisActions";
import * as QUESTIONAIRE_ACTIONS from "../../../actions/questionaireActions";
import * as LOOKUPS_ACTIONS from '../../../actions/lookupsActions';
import * as USER_GROUPS from '../../../actions/userGroupsActions';
import * as KML_ACTIONS from '../../../actions/kmlUploadActions';
import * as TENANT_CLOUD_ACTIONS from "../../../actions/tenantCloudActions";
import _ from "lodash";
import { connect } from "react-redux";
import { Storage } from 'aws-amplify';
import GraphHtmlRenderer from "./GraphHtmlRenderer";

// Custom styles for the DatePicker component
const datePickerStyles = document.createElement('style');
datePickerStyles.innerHTML = `
    .react-datepicker-popper {
        z-index: 9999 !important;
    }
`;
document.head.appendChild(datePickerStyles);

const CustomDatePickerInput = React.forwardRef(({ value, onClick }, ref) => (
    <Input
        icon="calendar"
        iconPosition="left"
        placeholder="Select Date"
        onClick={onClick}
        value={value}
        ref={ref}
        style={{ borderRadius: '8px', borderColor: '#ccc' }}
    />
));

const QnaireAnalysisPage = (props) => {
    const [state, setState] = useState({
        loading: false,
        isViewFileOpen: false,
        downloadingCsv: false,
        queryLoading: false,
        exportingPdf: false,
    });
    const [selectedQnaire, setSelectedQnaire] = useState(null);
    const [lookupGraphType, setLookupGraphType] = useState(null);
    const [textGraphType, setTextGraphType] = useState(null);
    const [filterQuestion, setFilterQuestion] = useState([]);
    const [filterQuestions, setFilterQuestions] = useState([]);
    const [userGroup, setUserGroup] = useState(null);
    const [kmlFile, setKMLFile] = useState(null);
    const [kmlLocationQuestion, setKMLLocationQuestion] = useState(null);
    const [kmlDistanceOption, setKMLDistanceOption] = useState(null);
    const [kmlDistanceOptionValue, setKMLDistanceOptionValue] = useState(1);
    const [filterValue, setFilterValue] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [analyzeLoading, setAnalyzeLoading] = useState(false);
    const [graphHtmls, setGraphHtmls] = useState([]);
    const [uniquePerUser, setUniquePerUser] = useState(false);

    const hasPermissions = (category) => {
        return (
            !_.isEmpty(props.userPermissions) &&
            props.userPermissions.hasOwnProperty(category)
        );
    };

    const isAdmin = () => {
        return (
            hasPermissions("Other") && props.userPermissions["Other"].actions.admin
        );
    };

    const canReadQuestionnaireAnalysis = () => {
        return (hasPermissions('Deep Analysis') && props.userPermissions['Deep Analysis'].actions["VIEW QUESTIONNAIRE ANALYSIS"])
            || isAdmin();
    }

    const canCreateQuestionnaireAnalysis = () => {
        return hasPermissions('Deep Analysis') && canReadQuestionnaireAnalysis() && props.userPermissions['Deep Analysis'].actions["CREATE QUESTIONNAIRE ANALYSIS"]
            || isAdmin();
    }

    const questionnaireOptions = props.questionnaires
        .map(q => ({
            key: q.id,
            value: q,
            text: q.name || q.title || `Questionnaire ${q.id}`,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));

    const lookupGraphTypeOptions = [
        { key: 'bar', value: 'bar', text: 'Bar Chart' },
        { key: 'pie', value: 'pie', text: 'Pie Chart' },
        // { key: 'line', value: 'line', text: 'Line Chart' },
    ].sort((a, b) => a.text.localeCompare(b.text));

    const textGraphTypeOptions = [
        { key: 'bar', value: 'bar', text: 'Bar Chart' },
        { key: 'wordCloud', value: 'wordCloud', text: 'Word Cloud' },
    ];

    const filterQuestionOptions = [
        { key: 'none', value: null, text: 'None' },
        ...props.filterQuestion
            .map(q => ({
                key: q.id,
                value: q,
                text: q.question || `Question ${q.id}`,
            }))
            .sort((a, b) => a.text.localeCompare(b.text))
    ];

    const userGroupOptions = [
        { key: 'none', value: null, text: 'None' },
        ...props.userGroups
            .map(g => ({
                key: g.id,
                value: g,
                text: g.name || `Group ${g.id}`,
            }))
            .sort((a, b) => a.text.localeCompare(b.text))
    ];

    const kmlFileOptions = [
        { key: 'none', value: null, text: 'None' },
        ...props.kmlFiles
            .map(f => ({
                key: f.id,
                value: f,
                text: f.name || `File ${f.id}`,
            }))
            .sort((a, b) => a.text.localeCompare(b.text))
    ];

    // Get location questions from the selected questionnaire
    const locationQuestionOptions = [
        { key: 'none', value: null, text: 'Select Location Question' },
        ...(props.graphQuestions || [])
            .filter(q => q.questionType === 'location')
            .map(q => ({
                key: q.id,
                value: q,
                text: q.question || `Question ${q.id}`,
            }))
            .sort((a, b) => a.text.localeCompare(b.text))
    ];

    const polygonRuleDistanceOptions =
        [
            { key: 'none', value: null, text: 'None' },
            // { key: 'along_boundary', value: 'along_boundary', text: 'Along the boundary of the polygon' },
            { key: 'any_pin', value: 'any_pin', text: 'Any PIN (such as a school)' },
            { key: 'on_boundary', value: 'on_boundary', text: 'On or within the boundary of this polygon' },
            // { key: 'within_x_meters', value: 'within_x_meters', text: 'Within x meters of any point' },
            // { key: 'within_x_meters_polygon_boundary', value: 'within_x_meters_polygon_boundary', text: 'Within x meters of the boundary of the polygon' },
        ];

    useEffect(() => {
        const plotlyScriptSrc = "https://cdn.plot.ly/plotly-3.0.1.min.js";
        const alreadyLoaded = !!document.querySelector(`script[src="${plotlyScriptSrc}"]`);
        if (!alreadyLoaded) {
            const script = document.createElement("script");
            script.src = plotlyScriptSrc;
            script.async = false;
            // script.onload = () => console.log("Plotly loaded");
            document.body.appendChild(script);
        }
    }, [selectedQnaire]);

    useEffect(() => {
        if (!_.isEmpty(props.tenant)) {
            if (props.isPermissionsFetched) {
                if (canReadQuestionnaireAnalysis()) {
                    props.fetchUserGroupsAction(props.tenant);
                    props.fetchKmlFilesAction(props.tenant);
                    props.fetchTenantCloudsAction();
                    props.fetchQuestionairesAction(props.tenant.id);
                } else {
                    toast.error('You do not have permission to view user groups.');
                }
            }
        }
    }, [props.tenant]);

    const fetchQnaireGraphsFromS3 = async () => {
        if (!_.isEmpty(selectedQnaire)) {
            // Check if query is available
            if (!props.qnaireAnalysisQuery || props.qnaireAnalysisQuery.length === 0) {
                setState(s => ({ ...s, loading: false }));
                return;
            }

            try {
                const basePath = `${process.env.REACT_APP_ENV}/plots/${selectedQnaire.tenantId}/${selectedQnaire.id}/${props.qnaireAnalysisQuery[0].id}/images`;
                const listResult = await Storage.list(basePath, { level: 'public' });
                const htmlFiles = listResult
                    ? listResult.filter(file => file.key.endsWith('.html'))
                    : (listResult.filter ? listResult.filter(file => file.key.endsWith('.html')) : []);
                if (!htmlFiles.length) {
                    toast.warning(
                        <>
                            No data found for this questionnaire.
                            <br />
                            Please run the analysis.
                        </>
                    );
                    setGraphHtmls([]);
                    setState(s => ({ ...s, loading: false }));
                    return;
                }
                const htmlFileData = await Promise.all(
                    htmlFiles.map(async (file) => {
                        const url = await Storage.get(file.key);
                        const response = await fetch(url);
                        if (!response.ok) {
                            toast.error(`Failed to fetch file: ${file.key}`);
                            return null;
                        }
                        const html = await response.text();
                        const match = file.key.match(/\/([^\/]+)\.html$/);
                        const name = match ? match[1] : file.key;
                        return {
                            html,
                            name,
                            lastModified: file.lastModified,
                            key: file.key,
                        };
                    })
                );
                setGraphHtmls(htmlFileData.filter(Boolean));
                setState(s => ({ ...s, loading: false }));
            } catch (error) {
                setGraphHtmls([]);
                setState(s => ({ ...s, loading: false }));
                console.error("Error fetching HTML files from S3:", error.message);
            }
        }
    };

    const downloadCsvFromS3 = async () => {
        if (!selectedQnaire) {
            toast.error("Please select a questionnaire first.");
            return;
        }

        if (!props.qnaireAnalysisQuery || props.qnaireAnalysisQuery.length === 0) {
            toast.error("Analysis query not available. Please wait for the data to load.");
            return;
        }

        setState(s => ({ ...s, downloadingCsv: true }));

        try {
            const basePath = `${process.env.REACT_APP_ENV}/plots/${selectedQnaire.tenantId}/${selectedQnaire.id}/${props.qnaireAnalysisQuery[0].id}/datasets/all_answers.csv`;
            const listResult = await Storage.list(basePath, { level: 'public' });

            // Look for CSV files in the data folder
            const csvFiles = listResult
                ? listResult.filter(file => file.key.endsWith('.csv'))
                : (listResult.filter ? listResult.filter(file => file.key.endsWith('.csv')) : []);

            if (!csvFiles.length) {
                toast.warning("No CSV data files found for this questionnaire. Please run the analysis first.");
                setState(s => ({ ...s, downloadingCsv: false }));
                return;
            }

            // If multiple CSV files exist, download the most recent one or all of them
            if (csvFiles.length === 1) {
                // Download single CSV file
                const csvFile = csvFiles[0];
                const url = await Storage.get(csvFile.key);

                // Create a temporary link to download the file
                const link = document.createElement('a');
                link.href = url;
                link.download = `questionnaire_${selectedQnaire.id}_data.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                toast.success("CSV file downloaded successfully!");
            } else {
                // Multiple CSV files - download the most recent one
                const mostRecentCsv = csvFiles.reduce((latest, current) => {
                    return (!latest.lastModified || current.lastModified > latest.lastModified) ? current : latest;
                });

                const url = await Storage.get(mostRecentCsv.key);

                // Create a temporary link to download the file
                const link = document.createElement('a');
                link.href = url;
                link.download = `questionnaire_${selectedQnaire.id}_data.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                toast.success("Most recent CSV file downloaded successfully!");
            }

        } catch (error) {
            console.error("Error downloading CSV from S3:", error);
            toast.error("Failed to download CSV file. Please try again.");
        } finally {
            setState(s => ({ ...s, downloadingCsv: false }));
        }
    };

    const exportGraphsAsPDF = async () => {
        if (graphHtmls.length === 0) {
            toast.error("No graphs available to export.");
            return;
        }

        setState(s => ({ ...s, exportingPdf: true }));
        toast.info("Generating PDF... This may take a moment.");

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const spacing = 5;
            const graphWidth = (pageWidth - 2 * margin - spacing) / 2;
            let currentY = margin;
            let currentX = margin;
            let graphsInRow = 0;
            let maxRowHeight = 0;

            for (let i = 0; i < graphHtmls.length; i++) {
                const graphContainer = document.getElementById(`graph-container-${i}`);
                if (graphContainer) {
                    const canvas = await html2canvas(graphContainer, { scale: 1 });
                    const imgData = canvas.toDataURL('image/jpeg', 0.7);
                    let imgHeight = (canvas.height * graphWidth) / canvas.width;

                    if (imgHeight > 70) {
                        imgHeight = 70;
                    }

                    if (currentY + imgHeight > pageHeight - margin) {
                        pdf.addPage();
                        currentY = margin;
                        currentX = margin;
                        graphsInRow = 0;
                        maxRowHeight = 0;
                    }

                    pdf.addImage(imgData, 'JPEG', currentX, currentY, graphWidth, imgHeight);
                    maxRowHeight = Math.max(maxRowHeight, imgHeight);
                    graphsInRow++;

                    if (graphsInRow >= 2) {
                        currentY += maxRowHeight + spacing;
                        currentX = margin;
                        graphsInRow = 0;
                        maxRowHeight = 0;
                    } else {
                        currentX += graphWidth + spacing;
                    }
                }
            }

            pdf.save(`questionnaire_analysis_${selectedQnaire?.id || 'export'}_${Date.now()}.pdf`);
            toast.success("PDF exported successfully!");
        } catch (error) {
            console.error("Error exporting PDF:", error);
            toast.error("Failed to export PDF. Please try again.");
        } finally {
            setState(s => ({ ...s, exportingPdf: false }));
        }
    };

    const refreshQnaireData = () => {
        if (selectedQnaire) {
            setGraphHtmls([]);
            toast.info(`Checking for questionnaire analysis data...`);
            setState({
                ...state,
                loading: true,
                queryLoading: true,
            });
            props.fetchQuestionnaireQuestions(selectedQnaire.id, null, selectedQnaire.tenantId);
            props.fetchQuestionnaireAnalysis(selectedQnaire.id, selectedQnaire.tenantId);
        }
    };

    useEffect(() => {
        if (selectedQnaire, props.qnaireAnalysisQuery) {
            refreshQnaireData();
        }
    }, [selectedQnaire]);

    useEffect(() => {
        if (props.qnaireAnalysisQuery && props.qnaireAnalysisQuery.length > 0) {
            const query = props.qnaireAnalysisQuery[0];
            setLookupGraphType(query.lookupGraphType);
            setTextGraphType(query.textGraphType);
            setFilterQuestions(query.filterQuestions || []);
            setStartDate(query.startDate ? new Date(query.startDate) : null);
            setEndDate(query.endDate ? new Date(query.endDate) : null);

            // Find the full user group object from props.userGroups using the userGroupId
            const foundUserGroup = query.userGroupId
                ? props.userGroups.find(group => group.id === query.userGroupId)
                : null;
            setUserGroup(foundUserGroup || null);

            // Find the full KML file object from props.kmlFiles using the kmlFileId
            const foundKmlFile = query.kmlFileId
                ? props.kmlFiles.find(file => file.id === query.kmlFileId)
                : null;
            setKMLFile(foundKmlFile || null);

            // Find the location question from props.graphQuestions using the kmlLocationQuestionId
            const foundKmlLocationQuestion = query.kmlLocationQuestionId
                ? props.graphQuestions.find(q => q.id === query.kmlLocationQuestionId)
                : null;
            setKMLLocationQuestion(foundKmlLocationQuestion || null);

            setKMLDistanceOption(query.kmlDistanceOption || null);
            setKMLDistanceOptionValue(query.kmlDistanceOptionValue || 1);

            // Now fetch graphs since query is available
            setState(s => ({ ...s, queryLoading: false }));
            fetchQnaireGraphsFromS3();
        } else {
            setLookupGraphType(null);
            setTextGraphType(null);
            setFilterQuestions([]);
            setStartDate(null);
            setEndDate(null);
            setUserGroup(null);
            setKMLFile(null);
            setKMLLocationQuestion(null);
            setKMLDistanceOption(null);
            setKMLDistanceOptionValue(1);
            setState(s => ({ ...s, queryLoading: false, loading: false }));
        }
    }, [props.qnaireAnalysisQuery, props.userGroups, props.kmlFiles, selectedQnaire]);


    const analyseQnaire = async () => {
        // Validation: If location question is selected, KML file must also be selected
        if (kmlLocationQuestion && !kmlFile) {
            toast.error("Please select a KML file to compare with the location question.");
            return;
        }

        setGraphHtmls([]);
        setAnalyzeLoading(true);

        // Find all tenant clouds this tenant is part of AND the questionnaire is assigned to
        const tenantClouds = props.tenantClouds.filter(cloud =>
            cloud.tenants && cloud.tenants.some(tenant => tenant.id === props.tenant.id) &&
            cloud.questionnaires && cloud.questionnaires.some(questionnaire => questionnaire.id === selectedQnaire.id)
        );

        // Get all unique tenants from those clouds (excluding the current tenant)
        const cloudTenants = [];
        tenantClouds.forEach(cloud => {
            if (cloud.tenants) {
                cloud.tenants.forEach(tenant => {
                    cloudTenants.push(tenant);
                });
            }
        });

        const payload = {
            questionnaireId: selectedQnaire.id,
            lookupGraphType,
            textGraphType,
            tenantId: selectedQnaire.tenantId,
            tenantName: props.tenant.name,
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null,
            userGroupId: userGroup ? userGroup.id : null,
            cloudTenants: cloudTenants,
            uniquePerUser: uniquePerUser
        };
        if (kmlFile) {
            payload.kmlFileId = kmlFile.id;
            payload.kmlName = kmlFile.name;
            payload.kmlMarkers = kmlFile.kmlMarkers || [];
            payload.kmlPolygons = kmlFile.kmlPolygons || [];
            payload.kmlPolylines = kmlFile.kmlPolylines || [];
            payload.kmlDistanceOption = kmlDistanceOption || null;
            payload.kmlDistanceOptionValue = kmlDistanceOptionValue ? parseFloat(kmlDistanceOptionValue) : 1;

            // Add the selected location question ID
            if (kmlLocationQuestion) {
                payload.kmlLocationQuestionId = kmlLocationQuestion.id;
            }
        }
        if (filterQuestions && filterQuestions.length > 0) {
            payload.filterQuestions = filterQuestions;
        }
        try {
            let isInitiated = await props.analyseQnaireAction(payload);
            if (isInitiated) {
                toast.success("Analysis Successfully Initiated");
                toast.info("Please wait 30 minutes for the analysis to complete.")
            } else {
                toast.error("Failed to initiate analysis. Please try again");
            }
        } catch (error) {
            toast.error("Failed to initiate analysis. Please try again");
        } finally {
            setAnalyzeLoading(false);
        }
    };

    const handleDateChange = (type, date) => {
        if (type === 'startDate') setStartDate(date);
        if (type === 'endDate') setEndDate(date);
    };

    const getQuestionTextById = (id) => {
        if (!props.graphQuestions || !Array.isArray(props.graphQuestions)) return '';
        const q = props.graphQuestions.find(q => q.id === id);
        return q ? q.question : '';
    };

    return (
        <Form>
            <Segment>
                <Grid>
                    <Grid.Row>
                        <Grid.Column width={14}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <h3>Questionnaire Analysis</h3>
                            </div>
                        </Grid.Column>
                        <Grid.Column floated="right" width={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Form.Button compact primary icon='refresh' size='small' floated='right'
                                loading={analyzeLoading}
                                disabled={selectedQnaire === null || analyzeLoading}
                                onClick={(e) => {
                                    e.preventDefault();
                                    refreshQnaireData();
                                }}></Form.Button>
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column width={5}>
                            <Label style={{ marginBottom: '8px' }}>Questionnaire To Analyse:</Label>
                            <Dropdown
                                placeholder="Select Questionnaire"
                                fluid
                                search
                                selection
                                options={questionnaireOptions}
                                value={selectedQnaire}
                                onChange={(e, { value }) => {
                                    const questionnaire = props.questionnaires.find(q => q.id === value.id);
                                    setSelectedQnaire(questionnaire);
                                    // Reset KML-related selections when questionnaire changes
                                    setKMLLocationQuestion(null);
                                    setKMLFile(null);
                                    setKMLDistanceOption(null);
                                    setKMLDistanceOptionValue(1);
                                }}
                                style={{ zIndex: 2, position: 'relative' }}
                            />
                        </Grid.Column>
                        <Grid.Column width={4}>
                            <Label style={{ marginBottom: '8px' }}>Graph Type for Lookup Questions:</Label>
                            <Dropdown
                                placeholder="Select Graph Type"
                                fluid
                                selection
                                options={lookupGraphTypeOptions}
                                value={lookupGraphType}
                                onChange={(e, { value }) => setLookupGraphType(value)}
                            />
                        </Grid.Column>
                        <Grid.Column width={4}>
                            <Label style={{ marginBottom: '8px' }}>Graph Type for Text-based Questions:</Label>
                            <Dropdown
                                placeholder="Select Graph Type"
                                fluid
                                selection
                                options={textGraphTypeOptions}
                                value={textGraphType}
                                onChange={(e, { value }) => setTextGraphType(value)}
                            />
                        </Grid.Column>
                        <Grid.Column width={2}>
                            <div style={{ marginTop: '32px' }}>
                                <Checkbox
                                    toggle
                                    label="Unique per user"
                                    checked={uniquePerUser}
                                    onChange={(e, { checked }) => setUniquePerUser(checked)}
                                />
                            </div>
                        </Grid.Column>
                    </Grid.Row>
                    <Divider />
                    <Grid.Row>
                        <Grid.Column width={6}>
                            <Label style={{ marginBottom: '8px' }}>Filter Question:</Label>
                            <Dropdown
                                placeholder="Select Question"
                                fluid
                                selection
                                search
                                options={filterQuestionOptions}
                                value={""}
                                onChange={(e, { value }) => {
                                    setFilterQuestion(value);
                                    if (value) {
                                        props.fetchQuestionLookup(value);
                                        setShowFilterModal(true);
                                    }
                                }}
                            />
                        </Grid.Column>
                        <Grid.Column width={4}>
                            <Label style={{ marginBottom: '8px' }}>User Group:</Label>
                            <Dropdown
                                placeholder="Select Group"
                                fluid
                                selection
                                search
                                options={userGroupOptions}
                                value={userGroup}
                                onChange={(e, { value }) => {
                                    setUserGroup(value);
                                }}
                            />
                        </Grid.Column>
                        <Grid.Column width={3}>
                            <Grid.Column>
                                <Label style={{ marginBottom: '8px' }}>Start Date:</Label>
                            </Grid.Column>
                            <Grid.Column >
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => handleDateChange('startDate', date)}
                                    endDate={endDate}
                                    maxDate={endDate}
                                    dateFormat="dd-MM-yyyy"
                                    isClearable
                                    customInput={<CustomDatePickerInput />}
                                    popperClassName="datepicker-popper"
                                    popperPlacement="bottom-start"
                                    popperModifiers={{
                                        preventOverflow: {
                                            enabled: true,
                                            escapeWithReference: false,
                                            boundariesElement: 'viewport'
                                        }
                                    }}
                                />
                            </Grid.Column>
                        </Grid.Column>
                        <Grid.Column width={3}>
                            <Grid.Column >
                                <Label style={{ marginBottom: '8px' }}>End Date:</Label>
                            </Grid.Column>
                            <Grid.Column>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => handleDateChange('endDate', date)}
                                    startDate={startDate}
                                    minDate={startDate}
                                    dateFormat="dd-MM-yyyy"
                                    isClearable
                                    customInput={<CustomDatePickerInput />}
                                    popperClassName="datepicker-popper"
                                    popperPlacement="bottom-start"
                                    popperModifiers={{
                                        preventOverflow: {
                                            enabled: true,
                                            escapeWithReference: false,
                                            boundariesElement: 'viewport'
                                        }
                                    }}
                                />
                            </Grid.Column>
                        </Grid.Column>
                    </Grid.Row>
                    {filterQuestions && filterQuestions.length > 0 && (
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <div style={{ padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                    <Label style={{ marginBottom: '8px' }}>Selected Filter(s):</Label>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid #ddd' }}>
                                                <th style={{ padding: '8px', textAlign: 'left' }}>Question</th>
                                                <th style={{ padding: '8px', textAlign: 'left' }}>Value(s)</th>
                                                <th style={{ padding: '8px', textAlign: 'center', width: '120px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filterQuestions.map((fq, index) => {
                                                const question = props.filterQuestion.find(q => q.id === fq.id);
                                                return (
                                                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                                        <td style={{ padding: '8px' }}>{question?.question || `Question ${fq.id}`}</td>
                                                        <td style={{ padding: '8px' }}>
                                                            {Array.isArray(fq.value) ? (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                    {fq.value.map((val, i) => (
                                                                        <div key={i} style={{ backgroundColor: '#3A95A4', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '12px' }}>
                                                                            {val}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div style={{ backgroundColor: '#3A95A4', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '12px', display: 'inline-block' }}>
                                                                    {fq.value}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                                            <Button.Group size="mini">
                                                                <Button
                                                                    icon="edit"
                                                                    color="blue"
                                                                    onClick={() => {
                                                                        setFilterQuestion(question);
                                                                        setFilterValue(fq.value);
                                                                        props.fetchQuestionLookup(question);
                                                                        setShowFilterModal(true);
                                                                    }}
                                                                />
                                                                <Button
                                                                    icon="trash"
                                                                    color="red"
                                                                    onClick={() => {
                                                                        const updatedFilters = filterQuestions.filter((_, i) => i !== index);
                                                                        setFilterQuestions(updatedFilters);
                                                                        if (filterQuestion && filterQuestion.id === fq.id) {
                                                                            setFilterValue(null);
                                                                        }
                                                                    }}
                                                                />
                                                            </Button.Group>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    {selectedQnaire && (
                        <>
                            <Divider />
                            <Grid.Row>
                                <Grid.Column width={4}>
                                    <Label style={{ marginBottom: '8px' }}>Location Question:</Label>
                                    <Dropdown
                                        placeholder="Select Location Question"
                                        fluid
                                        selection
                                        search
                                        options={locationQuestionOptions}
                                        value={kmlLocationQuestion}
                                        onChange={(e, { value }) => {
                                            setKMLLocationQuestion(value);
                                            // Reset KML file when location question changes
                                            setKMLFile(null);
                                            setKMLDistanceOption(null);
                                        }}
                                    />
                                    {locationQuestionOptions.length <= 1 && (
                                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                            No location questions found in this questionnaire
                                        </div>
                                    )}
                                </Grid.Column>
                                {kmlLocationQuestion && (
                                    <Grid.Column width={4}>
                                        <Label style={{ marginBottom: '8px' }}>KML File:</Label>
                                        <Dropdown
                                            placeholder="Select KML"
                                            fluid
                                            selection
                                            search
                                            options={kmlFileOptions}
                                            value={kmlFile}
                                            onChange={(e, { value }) => {
                                                setKMLFile(value);
                                                // Reset distance options when KML file changes
                                                setKMLDistanceOption(null);
                                            }}
                                        />
                                    </Grid.Column>
                                )}
                                {(kmlLocationQuestion && kmlFile) && (
                                    <Grid.Column width={4} style={{ paddingBottom: "10px" }}>
                                        <Label style={{ marginBottom: '8px' }}>Distance Options:</Label>
                                        <Dropdown
                                            placeholder="Select distance option"
                                            fluid
                                            selection
                                            options={polygonRuleDistanceOptions}
                                            value={kmlDistanceOption || ''}
                                            onChange={(e, { value }) => {
                                                setKMLDistanceOption(value);
                                            }}
                                        />
                                    </Grid.Column>
                                )}
                                {(kmlLocationQuestion && kmlFile && (kmlDistanceOption === 'within_x_meters' || kmlDistanceOption === "within_x_meters_polygon_boundary")) && (
                                    <Grid.Column width={2} style={{ paddingBottom: "10px" }}>
                                        <Label style={{ marginBottom: '8px' }}>Distance (meters):</Label>
                                        <Input
                                            fluid
                                            type="number"
                                            placeholder="Enter distance"
                                            value={kmlDistanceOptionValue || ''}
                                            onChange={(e) => setKMLDistanceOptionValue(e.target.value)}
                                            min="1"
                                        />
                                    </Grid.Column>
                                )}
                            </Grid.Row>
                        </>
                    )}
                    <Grid.Column floated="right" width={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {canCreateQuestionnaireAnalysis() &&
                            <Form.Button
                                primary
                                fluid
                                loading={analyzeLoading}
                                style={{ marginTop: '24px' }}
                                onClick={analyseQnaire}
                                disabled={selectedQnaire === null || lookupGraphType === null || analyzeLoading || (kmlLocationQuestion && !kmlFile)}
                            >
                                Analyse
                            </Form.Button>
                        }
                    </Grid.Column>
                </Grid>
                <Modal open={showFilterModal} onClose={() => setShowFilterModal(false)} size="small">
                    <Header>Select Filter Value</Header>
                    <Modal.Content>
                        {(() => {
                            if (!filterQuestion) return <div>Please select a filter question.</div>;
                            const fileterQuestionId = filterQuestion.id;
                            const question = props.filterQuestion.find(q => q.id === fileterQuestionId);
                            if (!question) return null;

                            const renderOptions = () => {
                                if (question.questionType === 'yes_no') {
                                    return (
                                        <div>
                                            <Radio
                                                label="Yes"
                                                name="filterValue"
                                                value="Yes"
                                                checked={filterValue === 'Yes'}
                                                onChange={(e, { value }) => setFilterValue(value)}
                                                style={{ display: 'block', marginBottom: '10px' }}
                                            />
                                            <Radio
                                                label="No"
                                                name="filterValue"
                                                value="No"
                                                checked={filterValue === 'No'}
                                                onChange={(e, { value }) => setFilterValue(value)}
                                            />
                                        </div>
                                    );
                                }

                                // if ((question.questionType === 'mc_sel_one' || question.questionType === 'multi_select') && props.questionLookup?.length > 0 && props.questionLookup[0]?.values) { //Remove the multi_select check if you want to support multi-select filtering
                                if (question.questionType === 'mc_sel_one' && props.questionLookup?.length > 0 && props.questionLookup[0]?.values) {
                                    const options = JSON.parse(props.questionLookup[0].values);
                                    return options.map((option, index) => (
                                        <Radio
                                            key={index}
                                            label={option.value}
                                            name="filterValue"
                                            value={option.value}
                                            checked={filterValue === option.value}
                                            onChange={(e, { value }) => setFilterValue(value)}
                                            style={{ display: 'block', marginBottom: '10px' }}
                                        />
                                    ));
                                }

                                // For the future.at the moment we only filter on a single value for multi_select
                                if (question.questionType === 'multi_select' && props.questionLookup?.length > 0 && props.questionLookup[0]?.values) {
                                    const options = JSON.parse(props.questionLookup[0].values);
                                    return options.map((option, index) => (
                                        <Checkbox
                                            key={index}
                                            label={option.value}
                                            checked={Array.isArray(filterValue) ? filterValue.includes(option.value) : false}
                                            onChange={(e, { checked }) => {
                                                const currentValues = Array.isArray(filterValue) ? filterValue : [];
                                                if (checked) {
                                                    setFilterValue([...currentValues, option.value]);
                                                } else {
                                                    setFilterValue(currentValues.filter(v => v !== option.value));
                                                }
                                            }}
                                            style={{ display: 'block', marginBottom: '10px' }}
                                        />
                                    ));
                                }

                                return <div>No options available</div>;
                            };

                            return (
                                <div>
                                    <p><strong>{question.question}</strong></p>
                                    {renderOptions()}
                                </div>
                            );
                        })()
                        }
                    </Modal.Content>
                    <Modal.Actions>
                        <Button primary onClick={() => {
                            setShowFilterModal(false);
                            if (filterQuestion && filterValue) {
                                // Add the question and selected value to filterQuestions list
                                const filterObj = {
                                    id: filterQuestion.id,
                                    value: filterValue
                                };

                                // Check if this question is already in the list and update it
                                const existingIndex = filterQuestions.findIndex(q => q.id === filterQuestion.id);
                                if (existingIndex >= 0) {
                                    const updatedList = [...filterQuestions];
                                    updatedList[existingIndex] = filterObj;
                                    setFilterQuestions(updatedList);
                                } else {
                                    setFilterQuestions([...filterQuestions, filterObj]);
                                }

                                console.log("Selected filter value:", filterValue);
                                console.log("Updated filterQuestions list:", [...filterQuestions, filterObj]);
                            }
                        }}>close</Button>
                    </Modal.Actions>
                </Modal>
            </Segment>
            <Segment style={{ minHeight: 120, position: 'relative', zIndex: 0 }}>
                <Grid stackable columns={2}>
                    <Grid.Row>
                        <Grid.Column width={14}>
                            <div>
                                <h3>Questionnaire Analytics</h3>
                                {props.qnaireAnalysisQuery && props.qnaireAnalysisQuery.length > 0 && props.qnaireAnalysisQuery[0].createdAt && (
                                    <Label color="blue" style={{ marginTop: '5px' }}>
                                        Last Analysis: {(() => {
                                            const dateStr = props.qnaireAnalysisQuery[0].createdAt;
                                            const year = dateStr.substring(0, 4);
                                            const month = dateStr.substring(4, 6);
                                            const day = dateStr.substring(6, 8);
                                            const hour = dateStr.substring(9, 11);
                                            const minute = dateStr.substring(11, 13);
                                            const second = dateStr.substring(13, 15);
                                            const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
                                            return date.toLocaleString();
                                        })()}
                                    </Label>
                                )}
                            </div>
                        </Grid.Column>
                        <Grid.Column floated="right" width={4} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', gap: '8px' }}>
                            <Button
                                compact
                                primary
                                size='small'
                                loading={state.downloadingCsv}
                                disabled={selectedQnaire === null || state.downloadingCsv || state.queryLoading || !props.qnaireAnalysisQuery || props.qnaireAnalysisQuery.length === 0}
                                onClick={(e) => {
                                    e.preventDefault();
                                    downloadCsvFromS3();
                                }}
                                title="Download CSV Data"
                            >Download CSV</Button>
                            <Button
                                compact
                                color='green'
                                size='small'
                                loading={state.exportingPdf}
                                disabled={graphHtmls.length === 0 || state.exportingPdf}
                                onClick={(e) => {
                                    e.preventDefault();
                                    exportGraphsAsPDF();
                                }}
                                title="Export Graphs as PDF"
                            >Export PDF</Button>
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        {state.loading || state.queryLoading ? (
                            <Grid.Column width={16} style={{ minHeight: 80, position: 'relative', zIndex: 0 }}>
                                <Dimmer active inverted style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
                                    <Loader>{state.queryLoading ? 'Loading analysis query...' : 'Loading...'}</Loader>
                                </Dimmer>
                            </Grid.Column>
                        ) : selectedQnaire == null ? (
                            <Grid.Column width={16} style={{ minHeight: 80 }}>
                                <div style={{ color: '#888', padding: '1em 0' }}>
                                    Please select a questionnaire to view its analytics.
                                </div>
                            </Grid.Column>
                        ) : graphHtmls.length === 0 ? (
                            <Grid.Column width={16} style={{ minHeight: 80 }}>
                                <div style={{ color: '#888', padding: '1em 0' }}>
                                    The questionnaire still needs to be analysed.
                                </div>
                            </Grid.Column>
                        ) : (
                            (() => {
                                // Sort graphHtmls to show yes_no questions first
                                const sortedGraphHtmls = [...graphHtmls].sort((a, b) => {
                                    const questionA = props.graphQuestions.find(q => q.id === a.name);
                                    const questionB = props.graphQuestions.find(q => q.id === b.name);

                                    const typeA = questionA?.questionType || '';
                                    const typeB = questionB?.questionType || '';

                                    // yes_no questions come first
                                    if (typeA === 'yes_no' && typeB !== 'yes_no') return -1;
                                    if (typeA !== 'yes_no' && typeB === 'yes_no') return 1;

                                    // Keep original order for same types
                                    return 0;
                                });

                                const items = [];
                                let colIdx = 0;
                                for (let i = 0; i < sortedGraphHtmls.length; i++) {
                                    const file = sortedGraphHtmls[i];
                                    const question = props.graphQuestions.find(q => q.id === file.name);
                                    const isLocation = question && (question.questionType === 'location' || question.questionType === 'mc_sel_one' || question.questionType === 'multi_select');
                                    if (isLocation) {
                                        items.push(
                                            <Grid.Column width={16} key={"graph-location-" + i} style={{ marginBottom: 24 }}>
                                                <div id={`graph-container-${i}`} style={{ border: '5px solid #eee', borderRadius: 6, padding: 8, background: '#fafafa', minHeight: 200, overflow: 'auto' }}>
                                                    <Label style={{ marginBottom: '8px', display: 'block' }}>
                                                        Question: {getQuestionTextById(file.name) || file.name}
                                                    </Label>
                                                    <div style={{ border: '1px solid #eee', borderRadius: 6, background: '#fafafa', minHeight: 200, overflow: 'auto' }}>
                                                        <GraphHtmlRenderer html={file.html} idx={i} />
                                                    </div>
                                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Label>
                                                            Last Modified:  {file.lastModified ? new Date(file.lastModified).toLocaleString() : ''}
                                                        </Label>
                                                    </div>
                                                </div>
                                            </Grid.Column>
                                        );
                                        colIdx = 0;
                                    } else {
                                        items.push(
                                            <Grid.Column width={8} key={"graph-" + i} style={{ marginBottom: 24, display: 'flex', flexDirection: 'column' }}>
                                                <div id={`graph-container-${i}`} style={{ border: '5px solid #eee', borderRadius: 6, padding: 8, background: '#fafafa', minHeight: 200, overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                    <Label style={{ marginBottom: '8px', display: 'block' }}>
                                                        Question: {getQuestionTextById(file.name) || file.name}
                                                    </Label>
                                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                        <GraphHtmlRenderer html={file.html} idx={i} />
                                                    </div>
                                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Label>
                                                            Last Modified:  {file.lastModified ? new Date(file.lastModified).toLocaleString() : ''}
                                                        </Label>
                                                    </div>
                                                </div>
                                            </Grid.Column>
                                        );
                                        colIdx++;
                                        if (colIdx === 2) colIdx = 0;
                                    }
                                }
                                return items;
                            })()
                        )}
                    </Grid.Row>
                </Grid>
            </Segment>
        </Form >
    );
};
const mapStateToProps = (state) => {
    const graphQuestionTypes = [
        'location',
        'yes_no',
        'multi_select',
        'mc_sel_one',
        'val_string',
        'val_paragraph',
    ];

    const filterQuestionTypes = [
        'yes_no',
        'multi_select',
        'mc_sel_one',
    ];

    return {
        userPermissions: _.isEmpty(state.authUserRoles.permissions)
            ? {}
            : state.authUserRoles.permissions,
        isPermissionsFetched: state.isPermissionsFetched === true,
        graphQuestions: Array.isArray(state.deepAnalysisQuestions)
            ? state.deepAnalysisQuestions.filter(q => graphQuestionTypes.includes(q.questionType))
            : [],
        filterQuestion: Array.isArray(state.deepAnalysisQuestions)
            ? state.deepAnalysisQuestions.filter(q => filterQuestionTypes.includes(q.questionType))
            : [],
        questionLookup: state.lookups || {},
        userGroups: state.userGroups || {},
        kmlFiles: state.kmlFiles || {},
        tenantClouds: state.tenantClouds || [],
        qnaireAnalysisQuery: state.qnaireAnalysisQuery || [],
    };
};

export default connect(
    mapStateToProps,
    // map dispatch to props
    (dispatch) => ({
        analyseQnaireAction: (payload) => dispatch(DEEPANALYSIS_ACTIONS.analyseQnaireAction(payload)),
        fetchQuestionnaireAnalysis: (questionnaireId, tenantId) => dispatch(DEEPANALYSIS_ACTIONS.fetchQuestionnaireAnalysisAction(questionnaireId, tenantId)),
        fetchQuestionairesAction: (payload) => dispatch(QUESTIONAIRE_ACTIONS.fetchQuestionairesAction(payload)),
        fetchQuestionnaireQuestions: (questionaireId, sectionId, tenantId) => dispatch(DEEPANALYSIS_ACTIONS.fetchQuestionsAction(questionaireId, sectionId, tenantId)),
        fetchUserGroupsAction: (tenant) => dispatch(USER_GROUPS.fetchUserGroupsAction(tenant)),
        fetchKmlFilesAction: (tenant) => dispatch(KML_ACTIONS.fetchKmlFilesAction(tenant)),
        fetchTenantCloudsAction: () => dispatch(TENANT_CLOUD_ACTIONS.fetchTenantCloudsAction()),
        fetchQuestionLookup: (question) => dispatch(LOOKUPS_ACTIONS.fetchLookupsAction(question)),
        clearDeepAnalysisQueryAction: () => dispatch(DEEPANALYSIS_ACTIONS.clearDeepAnalysisQueryAction()),
    })
)(QnaireAnalysisPage);
